import { APP_BASE_HREF, CommonModule, PlatformLocation } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { HttpClientModule } from '@angular/common/http';
import { InjectionToken, NgModule } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule } from '@angular/router';
import { NgbModule } from '@ng-bootstrap/ng-bootstrap';
import { PaymentDemoComponent } from 'src/app/components/eventregistration/paymentdemo/payment.demo';
import { ExternalProfileUrlResolver } from 'src/app/resolvers/external.profile.url.resolver';
import { AppComponent } from './components/app/app.component';
import { ErrorMessageComponent } from './components/errormessage/errormessage.component';
import { EventComponent } from './components/event/event.component';
import { PassesComponent } from './components/event/passes/passes.component';
import { SessionCartComponent } from './components/event/session-cart/session-cart.component';
import { SessionCartService } from './components/event/session-cart/session-cart.service';
import { SessionsComponent } from './components/event/sessions/sessions.component';
import { SessionsListViewComponent } from './components/event/sessionslistview/sessionslistview.component';
import { SessiontracksComponent } from './components/event/sessiontracks/sessiontracks.component';
import { SpeakersComponent } from './components/event/speakers/speakers.component';
import { SponsorsComponent } from './components/event/sponsors/sponsors.component';
import { AttendeeComponent } from './components/eventregistration/attendee/attendee.component';
import { CustomRegistrationFieldsComponent } from './components/eventregistration/attendee/custom-registration-fields/custom-registration-fields.component';
import { CaptchaComponent } from './components/eventregistration/captcha/captcha.component';
import { ConfirmationComponent } from './components/eventregistration/confirmation/confirmation.component';
import { EventRegistrationComponent } from './components/eventregistration/eventregistration.component';
import { EventRegistrationFormComponent } from './components/eventregistration/eventregistrationform/eventregistrationform.component';
import { RegistrationFormInitializerDirective } from './components/eventregistration/eventregistrationform/eventregistrationform.component';
import { FreeRegistrationComponent } from './components/eventregistration/free-registration/free.registration';
import { NgbdModalContentComponent } from './components/eventregistration/modal/modal.component';
import { PaymentPaypalComponent } from './components/eventregistration/paymentpaypal/payment.paypal';
import { RegistrationFinalizeComponent } from './components/eventregistration/registrationfinalize/registration.finalize';
import { SessionSelectionComponent } from './components/eventregistration/session-selection/session-selection.component';
import { HomeComponent } from './components/home/home.component';
import { MyRegistrationsComponent } from './components/myregistrations/myregistrations.component';
import { WaitlistItemCardComponent } from './components/myregistrations/waitlist-item-card/waitlist-item-card.component';
import { NavMenuComponent } from './components/navmenu/navmenu.component';
import { SafePipe } from './components/pipes/safe.pipe';
import { PreviewComponent } from './components/preview/preview.component';
import { SpinnerComponent } from './components/spinner/spinner.component';
import { DefaultImageDirective } from './directives/defaultImage.directive';
import { TranslateDirective } from './directives/translate.directive';
import { RequiredTrueValidatorDirective } from './directives/true-value.validator.directive';
import { IsAuthenticatedGuard } from './guards/is.authenticated.guard';
import { AadB2CHelper } from './helpers/AadB2CHelper';
import { ImageHelper } from './helpers/ImageHelper';
import { AadB2CHelperProvider } from './providers/aadb2c.helper.provider';
import { HttpHelperProvider } from './providers/http.helper.provider';
import {
  EventServiceProvider,
  LabelsServiceProvider,
  SessionServiceProvider,
  SponsorshipsServiceProvider,
  TracksServiceProvider,
  UserServiceProvider
} from './providers/service.providers';
import { ExternalLoginUrlResolver } from './resolvers/external.login.url.resolver';
import { ExternalLogoutUrlResolver } from './resolvers/external.logout.url.resolver';
import { ExternalUrlResolver } from './resolvers/external.url.resolver';
import { InternalUrlResolver } from './resolvers/internal.url.resolver';
import { AppRoutes } from './routes';
import { BrowserSessionService } from './services/browser-session.service';
import { CaptchaService } from './services/captcha.service';
import { CookieService } from './services/cookie.service';
import { LabelsService } from './services/labels.service';

export const BASE_APP_NAME = 'events-app';
@NgModule({
  bootstrap: [AppComponent],
  declarations: [
    AppComponent,
    NavMenuComponent,
    HomeComponent,
    PreviewComponent,
    EventComponent,
    EventRegistrationComponent,
    RegistrationFinalizeComponent,
    AttendeeComponent,
    SpeakersComponent,
    SessionsComponent,
    SessiontracksComponent,
    ConfirmationComponent,
    SpinnerComponent,
    ErrorMessageComponent,
    SponsorsComponent,
    PassesComponent,
    SessionsListViewComponent,
    CaptchaComponent,
    TranslateDirective,
    DefaultImageDirective,
    MyRegistrationsComponent,
    PaymentDemoComponent,
    PaymentPaypalComponent,
    FreeRegistrationComponent,
    CustomRegistrationFieldsComponent,
    SessionCartComponent,
    SessionSelectionComponent,
    WaitlistItemCardComponent,
    EventRegistrationFormComponent,
    RegistrationFormInitializerDirective,
    SafePipe,
    NgbdModalContentComponent,
    RequiredTrueValidatorDirective,
  ],
  imports: [
    BrowserModule,
    CommonModule,
    HttpClientModule,
    FormsModule,
    NgbModule,
    RouterModule.forRoot(
      AppRoutes,
      {
        scrollPositionRestoration: 'disabled',
        useHash: false,
        enableTracing: false
      }
    )
  ],
  providers: [
    IsAuthenticatedGuard,
    ExternalLoginUrlResolver,
    ExternalLogoutUrlResolver,
    ExternalProfileUrlResolver,
    ExternalUrlResolver,
    InternalUrlResolver,
    HttpClient,
    HttpHelperProvider,
    AadB2CHelperProvider,
    ImageHelper,
    CaptchaService,
    EventServiceProvider,
    SessionServiceProvider,
    TracksServiceProvider,
    UserServiceProvider,
    LabelsServiceProvider,
    SponsorshipsServiceProvider,
    BrowserSessionService,
    AadB2CHelper,
    SessionCartService,
    LabelsService,
    AadB2CHelper,
    CookieService,
    {
      provide: APP_BASE_HREF,
      useFactory: (platformLocation: PlatformLocation) => {
        // Get _app_base predefined my Magnolia
        let app_base = window['_app_base'] || window.location.pathname;

        // Add events-app as root Angular app name
        if (app_base.includes(BASE_APP_NAME)) {
          const eventAppRouteIndex = app_base.indexOf(BASE_APP_NAME);
          app_base = `${app_base.substring(0, eventAppRouteIndex)}${BASE_APP_NAME}`;
        } else {
          app_base += app_base?.endsWith('/') ? 'events-app' : '/events-app';
        }

        return app_base;
      },
      deps: [PlatformLocation],
    },
  ],
  entryComponents: [NgbdModalContentComponent],
})

export class AppModule {}
